// Copied from flobi-flow src/lib/inputConstraints.js (518142c9, 13 Oct) so Director's Generate drawer reads a model exactly as Flow's
// prompt bar does. Keep in step with Flow: change it there first, then copy it here.
/**
 * The limit one choice puts on another, as the catalogue sends it on an input (`modelInputData.constraints`):
 * `{ maxWhen: [{ key, value, max }] }`, "while `key` is `value`, this input goes to `max` at the most". A Fabric
 * pool model's Length carries it from what the model can make (MiniMax H3 at 768p stops at 9 s), so the node
 * never offers a pair the model can't make and no price has to know about it. A list of seconds ("9s") is
 * compared by its number. Every other input has none and is left exactly as it was.
 */

const secondsOf = (v) => {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  const m = typeof v === "string" ? /^\s*(\d+(?:\.\d+)?)\s*s?\s*$/i.exec(v) : null;
  return m ? Number(m[1]) : null;
};

/** The rules of an input, whichever shape it arrives in (a parsed composer input, or a catalogue entry). */
export function rulesOf(inp) {
  const c = inp?.constraints ?? inp?.modelInputData?.constraints ?? inp?.raw?.modelInputData?.constraints ?? null;
  const list = c && Array.isArray(c.maxWhen) ? c.maxWhen : [];
  return list.filter((r) => r && typeof r.key === "string" && r.value != null && Number.isFinite(Number(r.max)));
}

/** The most this input may be now, given the other values; null when nothing limits it. */
export function maxNow(inp, vals) {
  let most = null;
  for (const r of rulesOf(inp)) {
    if (String(vals?.[r.key] ?? "") !== String(r.value)) continue;
    const m = Number(r.max);
    most = most === null ? m : Math.min(most, m);
  }
  return most;
}

/** The values the input offers now: its own, less those past the limit the other values put on it. */
export function allowedValues(inp, vals) {
  const values = inp?.values || [];
  const most = maxNow(inp, vals);
  if (most === null) return values;
  return values.filter((v) => {
    const s = secondsOf(v);
    return s === null || s <= most + 1e-9;
  });
}

/** Inputs as the node shows them now: each list cut to what the other values allow. The same object when nothing is cut. */
export function constrainInputs(inputs, vals) {
  let changed = false;
  const out = (inputs || []).map((inp) => {
    if (!rulesOf(inp).length) return inp;
    const values = allowedValues(inp, vals);
    if (values.length === (inp.values || []).length) return inp;
    changed = true;
    return { ...inp, values };
  });
  return changed ? out : inputs;
}

/**
 * A value the limit no longer allows (768p picked while 12 s was set), as the one to set instead: the longest the
 * limit allows. Null when the value is fine, or when nothing is allowed.
 */
export function valueToFix(inp, vals) {
  const v = vals?.[inp.ref];
  if (v == null || v === "") return null;
  const allowed = allowedValues(inp, vals);
  if (!allowed.length || allowed.includes(v) || allowed.map(String).includes(String(v))) return null;
  const s = secondsOf(v);
  if (s === null) return null;
  const fits = allowed.filter((x) => (secondsOf(x) ?? Infinity) <= s);
  return fits.length ? fits[fits.length - 1] : allowed[0];
}
